import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { observer } from "mobx-react"
import { Button, List, Tag, Typography } from "antd"
import Table, { ColumnsType } from "antd/es/table"
import { choose } from "choose-matching"

import { balancesStore } from "@src/app/balanceStore"
import { LoadingBlock } from "@src/app/components/LoadingBlock"
import { Navigation } from "@src/app/components/navigation"
import { IdItem } from "@src/app/components/tableRowItems/idItem"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { paths } from "@src/app/paths"
import { PaymentsEntity } from "@src/app/paymentsStore/PaymentsEntity"
import { paymentsStore } from "@src/app/paymentsStore/store"
import { PaymentRefundEntity } from "@src/pages/refund/classes/PaymentRefundEntity"
import { GRefundPage } from "@src/pages/refund/classes/RefundPage"
import { Payment } from "@src/shared/api/generated"
import { TableExpandable } from "@src/shared/componemts/TableExpandable"
import { dateTimeFormat } from "@src/shared/date"
import { getExplorerUrl } from "@src/shared/getExplorerUrl"
import css from "./index.module.css"

const { Item } = List

type PaymentWithUrl = Payment & {
  tx_explorer_url: string
}

export const PaymentsPage = observer(() => {
  const [paymentsWithUrl, setPaymentsWithUrl] = useState([] as PaymentWithUrl[])

  const { id } = useParams()

  const { isLoading, loadCharge, name, payments } = paymentsStore

  const { currencies } = balancesStore

  const columns: ColumnsType<PaymentWithUrl> = [
    {
      dataIndex: "id",
      render: (_, { id }) => <IdItem id={id!} />,
      title: "ID",
    },
    {
      dataIndex: "detected_at",
      render: (_, { detected_at }) => dateTimeFormat(detected_at as string),
      title: "Date",
    },
    {
      dataIndex: "status",
      render: (_, { status }) => (
        <Tag color={status === "confirmed" ? "green" : "geekblue"}>
          {status}
        </Tag>
      ),
      title: "Status",
    },
    {
      align: "center",
      dataIndex: "amount",
      render: (_, { amount, symbol }) => {
        return <div>{amount}</div>
      },
      title: "Amount (crypto)",
    },
    {
      align: "center",
      dataIndex: "symbol",
      render: (_, { network, symbol }) => {
        const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
          network,
          symbol,
        })

        return (
          <Typography.Text>{currency?.symbol_alias ?? symbol}</Typography.Text>
        )
      },
      title: "Currency",
    },
    {
      align: "center",
      dataIndex: "Amount",
      render: (_, { exchange }) => {
        return exchange ? <div>{exchange.amount}</div> : "-"
      },
      title: "Amount Credited",
    },
    {
      align: "center",
      dataIndex: "symbol",
      render: (_, { exchange }) => {
        const currency = exchange
          ? GlobalCurrenciesCollection().getBySymbolNetwork({
              network: exchange.network!,
              symbol: exchange.symbol!,
            })
          : undefined

        return exchange ? (
          <div>{`${currency?.symbol_alias ?? exchange.symbol}`}</div>
        ) : (
          "-"
        )
      },
      title: "Currency",
    },
  ]

  useEffect(() => {
    loadCharge(id!)
  }, [id])

  useEffect(() => {
    setPaymentsWithUrl(getExplorerUrl(payments, currencies) as any)
  }, [currencies, payments])

  return (
    <div className={css.container}>
      <Navigation title="Payments" url={paths.transactions} />
      {choose(
        [isLoading, <LoadingBlock />],
        [
          payments?.length > 0,
          <Table
            columns={columns}
            dataSource={paymentsWithUrl}
            expandable={{
              expandedRowRender: (record) => (
                <ListExpandable
                  isLoading={isLoading}
                  payment={{ name, ...record }}
                />
              ),
            }}
            loading={isLoading}
          />,
        ],
      )}
    </div>
  )
})
function ListExpandable({
  isLoading,
  payment,
}: {
  isLoading: boolean
  payment: Payment & {
    name?: string
    tx_explorer_url: string
  }
}) {
  const {
    aml_level,
    amount,
    charge_id,
    detected_at,
    exchange,
    id,
    name,
    network,
    refund_id,
    status,
    symbol,
    tx_explorer_url,
    tx_id,
  } = payment

  const nav = useNavigate()

  const currency = GlobalCurrenciesCollection().getBySymbolNetwork({
    network,
    symbol,
  })

  const currencyExchange = exchange
    ? GlobalCurrenciesCollection().getBySymbolNetwork({
        network: exchange.network as string,
        symbol: exchange.symbol as string,
      })
    : undefined

  const makeRefund = () => {
    GRefundPage().setEntity(
      new PaymentRefundEntity(new PaymentsEntity(payment)),
    )

    nav(paths.refund)
  }

  return (
    <TableExpandable
      actions={
        status === "confirmed" && !refund_id ? (
          <Button
            id="GENERATED_ID_PAYMENTS_BUTTON_REFUND_1"
            onClick={makeRefund}
            size="large"
            type="primary"
          >
            Refund
          </Button>
        ) : undefined
      }
      isLoading={isLoading}
      list={[
        ["ID", id],
        ["Date", dateTimeFormat(detected_at as string)],
        ["Name", name],
        ["Amount", amount],
        ["Currency", symbol],
        ["Network", network],
        ["Status", status],
        ["AML level", aml_level],
        ["TxID", tx_id, { target: "_blank", to: tx_explorer_url + tx_id }],
        ...((exchange
          ? [
              [
                "Auto Converted to",
                currencyExchange?.symbol_alias ?? exchange.symbol,
              ],
              ["Status", exchange.status],
              ["Amount", exchange.amount],
              ["Currency", currencyExchange?.symbol_alias ?? exchange.symbol],
              ["Network", exchange.network],
            ]
          : []) as any),
        refund_id ? ["Refund ID", refund_id] : [],
      ]}
    />
  )
}
